import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FiSearch, FiFilter } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';
import MatchCard, { getSideLabel } from '../components/MatchCard/MatchCard';
import Modal from '../components/Modal/Modal';
import Pagination from '../components/Pagination/Pagination';
import p from './Pages.module.css';

export default function History() {
  const api = useApi();
  const toast = useToast();
  const PAGE_SIZE = 20;
  const [matches, setMatches] = useState([]);
  const [clasicos, setClasicos] = useState([]);
  const [matchSearch, setMatchSearch] = useState(''); const [clasicoSearch, setClasicoSearch] = useState('');
  const [entries, setEntries] = useState([]);
  const [title, setTitle] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [mPage, setMPage] = useState(1); const [mPages, setMPages] = useState(1); const [mTotal, setMTotal] = useState(0);
  const [cPage, setCPage] = useState(1); const [cPages, setCPages] = useState(1); const [cTotal, setCTotal] = useState(0);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');

  const buildQ = useCallback((page) => {
    let q = `?page=${page}&size=${PAGE_SIZE}`;
    if (dateFrom) q += `&createdFrom=${dateFrom}T00:00:00`;
    if (dateTo) q += `&createdTo=${dateTo}T23:59:59`;
    return q;
  }, [dateFrom, dateTo]);

  const loadMatches = useCallback(async (page = 1) => {
    try { const r = await api.get(`/v1/match/${buildQ(page)}`); if (r?.items) { setMatches(r.items); setMPage(r.page); setMPages(r.pages); setMTotal(r.total); } else if (Array.isArray(r)) setMatches(r); } catch {}
  }, [api, buildQ]);

  const loadClasicos = useCallback(async (page = 1) => {
    try { const r = await api.get(`/v1/clasico/${buildQ(page)}`); if (r?.items) { setClasicos(r.items); setCPage(r.page); setCPages(r.pages); setCTotal(r.total); } else if (Array.isArray(r)) setClasicos(r); } catch {}
  }, [api, buildQ]);

  useEffect(() => {
    loadMatches(1); loadClasicos(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const filteredM = useMemo(() => { if (!matchSearch) return matches; const l = matchSearch.toLowerCase(); return matches.filter(m => m.teams?.some(t => t.name.toLowerCase().includes(l))); }, [matches, matchSearch]);
  const filteredC = useMemo(() => { if (!clasicoSearch) return clasicos; const l = clasicoSearch.toLowerCase(); return clasicos.filter(c => c.teams?.some(t => t.name.toLowerCase().includes(l))); }, [clasicos, clasicoSearch]);

  const openHistory = async (item, type) => {
    const home = item.teams?.find(t => getSideLabel(t) === 'Home');
    const away = item.teams?.find(t => getSideLabel(t) === 'Away');
    const label = `${home?.name ?? '?'} vs ${away?.name ?? '?'}`;
    const endpoint = type === 'clasico' ? `/v1/quota-history/fictive-match/${item.id}` : `/v1/quota-history/match/${item.id}`;
    try {
      const result = await api.get(endpoint);
      setEntries(result || []);
      setTitle(`${type === 'clasico' ? 'Clasico' : 'Match'} History — ${label}`);
      setShowHistory(true);
    } catch { toast.error('Failed to load history'); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso); const pad = n => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth()+1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const fmtJson = (val) => {
    if (val == null) return '—';
    if (typeof val === 'string') { try { return JSON.stringify(JSON.parse(val), null, 2); } catch { return val; } }
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    return String(val);
  };

  return (
    <div className={p.page}>
      <div className={p.pageHeader}><h1 className={p.pageTitle}>History</h1><p className={p.pageSubtitle}>View change history for matches and clasicos</p></div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setShowDateFilter(v => !v)} className={p.btnOutline} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', padding: '5px 12px' }}>
          <FiFilter size={13} /> {showDateFilter ? 'Hide Filters' : 'Date Filter'}
        </button>
        {showDateFilter && <>
          <input type="date" className={p.formInput} style={{ width: 150, fontSize: '0.78rem', padding: '5px 8px' }} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setMPage(1); setCPage(1); }} />
          <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>to</span>
          <input type="date" className={p.formInput} style={{ width: 150, fontSize: '0.78rem', padding: '5px 8px' }} value={dateTo} onChange={e => { setDateTo(e.target.value); setMPage(1); setCPage(1); }} />
          {(dateFrom || dateTo) && <button className={p.btnOutline} style={{ fontSize: '0.72rem', padding: '4px 10px' }} onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>}
        </>}
      </div>

      <div className={p.columns}>
        <div>
          <div className={p.colHeader}><h3 className={p.colTitle}>Matches</h3><div style={{ position: 'relative' }}><FiSearch style={{ position: 'absolute', left: 8, top: 8, color: 'var(--text-muted)', fontSize: '0.8rem' }} /><input className={p.searchInput} style={{ paddingLeft: 28, minWidth: 160 }} placeholder="Search…" value={matchSearch} onChange={e => setMatchSearch(e.target.value)} /></div></div>
          <div className={p.list}>{filteredM.length > 0 ? filteredM.map(m => <MatchCard key={m.id} match={m} onItemClick={(x) => openHistory(x, 'match')} />) : <div className={p.emptyState}><div className={p.emptyText}>No matches</div></div>}</div>
          <Pagination page={mPage} pages={mPages} total={mTotal} size={PAGE_SIZE} onPageChange={pg => { setMPage(pg); loadMatches(pg); }} />
        </div>
        <div>
          <div className={p.colHeader}><h3 className={p.colTitle}>Clasicos</h3><div style={{ position: 'relative' }}><FiSearch style={{ position: 'absolute', left: 8, top: 8, color: 'var(--text-muted)', fontSize: '0.8rem' }} /><input className={p.searchInput} style={{ paddingLeft: 28, minWidth: 160 }} placeholder="Search…" value={clasicoSearch} onChange={e => setClasicoSearch(e.target.value)} /></div></div>
          <div className={p.list}>{filteredC.length > 0 ? filteredC.map(c => <MatchCard key={c.id} match={c} onItemClick={(x) => openHistory(x, 'clasico')} />) : <div className={p.emptyState}><div className={p.emptyText}>No clasicos</div></div>}</div>
          <Pagination page={cPage} pages={cPages} total={cTotal} size={PAGE_SIZE} onPageChange={pg => { setCPage(pg); loadClasicos(pg); }} />
        </div>
      </div>

      <Modal open={showHistory} onClose={() => setShowHistory(false)} title={title} wide>
        {entries.length === 0 ? (
          <div className={p.emptyState}><div className={p.emptyText}>No history available</div></div>
        ) : entries.map((entry, i) => (
          <div key={entry.id ?? i} className={p.card} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span className={`${p.badge} ${p.badgeStarted}`}>{entry.action ?? '—'}</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{fmtDate(entry.created)}</span>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
              {entry.changedBy && <span><strong>By:</strong> {entry.changedBy}</span>}
              {entry.entityType && <span><strong>Type:</strong> {entry.entityType}</span>}
              {entry.entityId != null && <span><strong>Entity ID:</strong> {entry.entityId}</span>}
            </div>
            {entry.changedFields && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8 }}>Changed: {Array.isArray(entry.changedFields) ? entry.changedFields.join(', ') : String(entry.changedFields)}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#e06c75', marginBottom: 4 }}>Old Values</div><pre style={{ fontSize: '0.72rem', color: 'var(--text-muted)', background: 'var(--bg-primary)', padding: 8, borderRadius: 6, overflow: 'auto', maxHeight: 200, margin: 0 }}>{fmtJson(entry.oldValues)}</pre></div>
              <div><div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#73c991', marginBottom: 4 }}>New Values</div><pre style={{ fontSize: '0.72rem', color: 'var(--text-muted)', background: 'var(--bg-primary)', padding: 8, borderRadius: 6, overflow: 'auto', maxHeight: 200, margin: 0 }}>{fmtJson(entry.newValues)}</pre></div>
            </div>
          </div>
        ))}
      </Modal>
    </div>
  );
}

